define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"src/c8-platform/service/aggregateStoreService",
	"csi/config",
	"csi/def",
	"csi/def/Action",
	"csi/reflection",
	"csi/store/_base",
	"csi/xml"
], function(declare, lang, _, {getCachedAggregateStoreDragFillId}, config, def, Action, reflection, csiStore, xml) {

	return declare(null, {
		/**@property {boolean} priorityStoreOnly - set to true, only present the priority store items */
		priorityStoreOnly: false,

		getCustomStoreKey: function(/*Context*/ctx, /*Heading*/h, /*Boolean*/bAll) {
			// summary: override default store key with custom one when necessary to improve caching of stores
			return null;
		},

		getStoreAction: function(/*Context*/ctx, /*Heading*/h, /*Object?*/resultParams) {
			if (this.priorityStoreOnly) {
				return this._getPriorityStoreAction(ctx, h);
			}

			// first try to get store from custom action
			var a = this._getStoreAction(ctx, h);
			if (!a) {
				// next try to use Heading Action, but first detect if filters have any expressions; that will indicate whether
				// store can be cached or not when context changes
				var sourceAction = ctx.ac ? ctx.ac.m_action : h.m_action;
				if (sourceAction && sourceAction.m_viewRef) {
					// create new action and only take necessary parameters to open dialog and extract result - don't want any extra processing
					a = lang.mixin(new Action(), {
						m_filtersView: sourceAction.m_filtersView,
						m_fixedQuery: sourceAction.m_fixedQuery,
						m_viewRef: sourceAction.m_viewRef
					});

					if (resultParams && a.m_fixedQuery && a.m_fixedQuery.hasExpression()) {
						// do not store this on action as the action may be reused against all nodes
						// and context can change per node
						resultParams.urlContext = ctx.getNodeURL();
					}
				}
			}

			return a;
		},

		getStoreKey: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return unique identifier for store that can indicate if store can be
			// reused in a given context; pass null for value since it does not factor into
			// key calculation
			return this.getCustomStoreKey(ctx, h, true) || this._createStore(ctx, h, null, true, true);
		},

		getStore: function(/*Context*/ctx, /*Heading*/h, /*Any*/value, /*Boolean*/bAll) {
			// summary: get store for heading
			// 		value - existing value for heading
			// 		bAll - if true, then return store used in "Go to Dialog" view (FIX: rename to bDialog?)
			return this._createStore(ctx, h, value, bAll, false, this.getCustomStoreKey(ctx, h, bAll));
		},

		getStoreOptions: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return special options to use when creating store
			return null;
		},

		getStoreRelationship: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return relationship to use for heading store
			return h.m_relationship;
		},

		/**
		 * @override
		 * Return true if ref attribute has an aggregate store
		 */
		hasAggregateStore(ctx, h) {
			return (h.m_action?.m_tag !== "AggregateInline") && reflection.hasAggregateStoreForAttribute(ctx.getNodeType(), h.m_id);
		},

		_createStore: function(/*Context*/ctx, /*Heading*/h, /*Any*/value, /*Boolean*/bAll, /*Boolean*/bKeyOnly, /*String?*/keyCustom) {
			// summary: if bKeyOnly, then return key for heading store; otherwise, return actual store for heading
			const node = ctx.getNode();
			const {m_id: id} = h;
			let filterFunc;
			let key;
			let priorityKey;
			let priorityStore;
			let result;
			let store;
			let storeOptions;

			// add support for AggregateTest function
			if (!bKeyOnly) {
				storeOptions = this.getStoreOptions(ctx, h) || {};
				storeOptions.cachingStore = ctx.cache;

				if (ctx.fd && ctx.fd.m_fAggregateTest) {
					// NOTE: this basically has to mirror FormData.canSelectNode, but in this case, selections should always
					// be allowed (m_bAllowSelections) and for inline editing selections so far are never omitted (m_bOmitSelections)
					console.assert(ctx.fd.m_bAllowSelections);
					console.assert(!ctx.fd.m_bOmitSelections);

					// customOperation is set to true so that we get the full node back rather than just its name; the full node is
					// usually needed for the test function
					storeOptions.customOperation = true;
					filterFunc = lang.hitch(this, this._testStoreResultNode, ctx.copyContext());
				}
			}

			// first try to get store from custom action
			var resultParams = {};
			var a = this.getStoreAction(ctx, h, resultParams);

			if (result = this._getStoreFromAction(ctx, h, a, storeOptions, bKeyOnly, keyCustom, resultParams.urlContext)) {
				key = result.key;
				store = result.store;
			}

			// now get store from relationship - if the main store above does not already exist, then this becomes the main store;
			// otherwise, it is treated as the priority store
			var r = this.getStoreRelationship(ctx, h);
			if (r && (!store || !store.priorityStore)) {
				if (keyCustom) {
					priorityKey = keyCustom;
				} else if (r.m_url && !r.hasFilterExpression()) {
					priorityKey = r.m_url;
				} else {
					priorityKey = node.$URL;
				}

				if (!bKeyOnly) {
					if (store) {
						priorityKey += "^Priority";
					}
					// only add existing value to main store, not priority store
					priorityStore = ctx.view.getHeadingStore(h, priorityKey) ||
						ctx.view.createHeadingNamesStore(ctx, h, null, r, priorityKey, storeOptions);

					if (priorityStore) {
						if (store) {
							store.priorityStore = priorityStore;
						} else {
							store = priorityStore;
							key = priorityKey;
						}
					}
				} else if (!key) {
					key = priorityKey;
				}
			}

			// return now if just looking for store key
			if (bKeyOnly) {
				// if no key defined, then check to see if there is an aggregate store key instead
				return !key ? getCachedAggregateStoreDragFillId(node.$URL, id) : key;
			}

			if (store) {
				if (this.priorityStoreOnly) {
					store.treatAsPriority = true;
					return store;
				}

				// check for priority store - do not use in conjunction with Heading Relationship
				if (!store.priorityStore) {
					a = this._getPriorityStoreAction(ctx, h);

					// do not pass existing value when creating priority store
					if (result = this._getStoreFromAction(ctx, h, a, storeOptions)) {
						store.priorityStore = result.store;
					}
				}

				// assign client-side filtering function - need to do even when reusing data store so that context is correct
				if (filterFunc) {
					store.filterFunc = filterFunc;

					if (store.priorityStore) {
						store.priorityStore.filterFunc = filterFunc;
					}
				}

				if (value != null) {
					storeOptions = csiStore.nameOptionsByHeading(ctx, h, value, storeOptions);
				}

				store.addCurrentValues(storeOptions.data);

				return store;
			}

			// create store with just existing value if no other store configured
			const defaultStoreOptions = {};

			// add aggregate store options if applicable to attribute
			if (this.hasAggregateStore(ctx, h)) {
				const params = this._getAggregateStoreParams(ctx, h);
				defaultStoreOptions.aggregateStoreParams = params;
				defaultStoreOptions.request = csiStore.getAggregateStoreRequest(params);

				// NOTE: although aggregate store returns currentValues, we still need to initialize the store with
				// the current value so that the widget can be created and considered valid before the query to the server
				// to get the currentValues and available values. This assumes that aggregateClass instances will share the
				// same names as the nodes they reference - for stores using aggregateClass. This makes sense as the links
				// displayed in the view should match those in the drop down. This seems to be the situation for all current
				// aggregateClass cases. Because of this, do not reset value to null.
			}

			store = ctx.view.createHeadingNamesStore(ctx, h, value, null, keyCustom || node?.$URL, defaultStoreOptions);

			return store;
		},

		_getPriorityStoreAction: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return Action including ViewRef and optionally Filter and FilterView to select priority items (which may or may not include allowed items below)
			return null;
		},

		_getStoreAction: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return Action including ViewRef and optionally Filter and FilterView to select allowed items
			return null;
		},

		_getStoreFromAction: function(/*Context*/ctx, /*Heading*/h, /*Action*/a, /*Object?*/storeOptions, /*Boolean*/bKeyOnly, /*String?*/keyCustom, /*String?*/urlContext) {
			// summary: convert given action into store
			var key = keyCustom;
			var store;

			if (a && a.m_viewRef) {
				// create store given action
				var vr = a.m_viewRef;
				var node = ctx.getNode();
				var nodeView = vr.getReferencedNode(ctx.cache, node);
				if (nodeView) {
					// change root node to that of view
					node = nodeView;

					// store key includes URL as well as view id - if action has filters that rely on current context,
					// then cache store relative to current node rather than view node
					if (!key) {
						key = (a.urlContext || urlContext || node.$URL) + "^" + vr.m_id;
					}

					if (!bKeyOnly) {
						// check if store already exists
						store = ctx.view.getHeadingStore(h, key);

						if (!store) {
							var defView = def.getNodeViewDef(nodeView, vr.m_id);
							var r = defView && defView.getPageRelationship();
							if (r) {
								r.resolve(null, reflection.getNodeClass(nodeView), nodeView);
								// apply any filters from action to relationship - make sure to use node in context rather than node owning view
								this._onActionStoreRelationship(ctx, h, a, r);

								ctx.pushParams().setNode(node);

								store = ctx.view.createHeadingNamesStore(ctx, h, null, r, key, storeOptions);
								store.action = a;
								ctx.pop();
							}
						}
					}
				}
			}

			return (bKeyOnly || store) ? {key: key, store: store} : null;
		},

		_onActionStoreRelationship: function(/*Context*/ctx, /*Heading*/h, /*Action*/a, /*Relationship*/r) {
			// summary: allow derived classes to override in case relationship needs to be modified in other ways
			r.addFilters(a.m_fixedQuery ? a.m_fixedQuery.getElements() : null, ctx.cache, ctx.getNode());
		},

		_testStoreResultNode: function(/*Context*/ctx, /*Object*/item) {
			// summary: return true if item from Names store can be selected according to AggregateTest property;
			// the empty item will not have a "node", so ignore it
			return !item.node || ctx.fd.m_fAggregateTest(ctx.setNode(item.node), ctx.fd);
		},

		/**
		 * @override
		 * Check if dependent attribute, and if so, if the depends-on attribute is non-empty
		 */
		hasValidDependency(ctx, h) {
			if (!this.inherited(arguments)) {
				return false;
			}

			const dependsOnInfo = h._rca?.getDependsOnInfo();
			if (!dependsOnInfo) {
				return true;
			}

			const dependsOnValue = ctx.getNodeAttributeValue(dependsOnInfo.attr.getId());
			return xml.isValidURL(dependsOnValue);
		}
	});
});